import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { adminApi, type OfflineQueueRow } from '../api/admin';

// M4-E2-S2 / §13.2 通知履歴 + §13.5 復旧通知ボタンの Web UI 等価。
// デバイス 接続時の自動 drain は廃止し、Slack 復旧通知ボタン or 本ページの
// 「今すぐ drain」「待機」で消化トリガを与える。

export function OfflineQueuePage() {
  const qc = useQueryClient();
  const [pendingOnly, setPendingOnly] = useState(false);
  const list = useQuery({
    queryKey: ['offline-queue', pendingOnly],
    queryFn: () => adminApi.listOfflineQueue({ pendingOnly, limit: 200 }),
    refetchInterval: 5_000,
  });
  const status = useQuery({
    queryKey: ['offline-queue', 'status'],
    queryFn: adminApi.offlineQueueStatus,
    refetchInterval: 5_000,
  });

  const pendingByDevice = useMemo(() => {
    const m = new Map<number, { device: { id: number; name: string }; count: number }>();
    for (const row of list.data ?? []) {
      if (row.deliveredAt) continue;
      const existing = m.get(row.targetDeviceId);
      if (existing) existing.count += 1;
      else m.set(row.targetDeviceId, { device: row.targetDevice, count: 1 });
    }
    return m;
  }, [list.data]);

  const remove = useMutation({
    mutationFn: adminApi.deleteOfflineQueueEntry,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['offline-queue'] }),
  });
  const drain = useMutation({
    mutationFn: adminApi.drainOfflineQueueForDevice,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['offline-queue'] }),
  });
  const pause = useMutation({
    mutationFn: adminApi.pauseOfflineQueueForDevice,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['offline-queue', 'status'] }),
  });

  const pausedSet = useMemo(
    () => new Set(status.data?.pausedDeviceIds ?? []),
    [status.data],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section className="k-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0 }}>Offline Queue</h2>
            <p className="k-muted" style={{ marginTop: 4 }}>
              デバイス が落ちている間に Slack 経由で来た問い合わせ。デバイス 復旧時に
              Slack channel に「処理する / 待機」ボタンが post される (§13.5)。
              ボタンを取り逃した場合は下の「今すぐ drain」で処理できる。
              <br />
              {status.data && (
                <>
                  未処理 <strong>{status.data.pendingCount}</strong> 件 / 配送済み{' '}
                  {status.data.deliveredCount} 件
                  {status.data.pausedDeviceIds.length > 0 && (
                    <>
                      {' '}/ 待機中 デバイス: [{status.data.pausedDeviceIds.join(', ')}]
                    </>
                  )}
                </>
              )}
            </p>
          </div>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={pendingOnly}
              onChange={(e) => setPendingOnly(e.target.checked)}
            />
            未処理のみ
          </label>
        </div>
      </section>

      {pendingByDevice.size > 0 && (
        <section className="k-card" style={{ background: '#fff7ed', borderColor: '#fdba74' }}>
          <h3 style={{ marginTop: 0 }}>:zap: デバイス 単位の保留</h3>
          <p className="k-muted" style={{ marginTop: 4 }}>
            Slack の復旧通知ボタンと同じ操作をここからも行えます。
          </p>
          <ul style={{ paddingLeft: 16, margin: 0 }}>
            {[...pendingByDevice.values()].map(({ device, count }) => (
              <li key={device.id} style={{ marginBottom: 8, listStyle: 'none' }}>
                <strong>{device.name}</strong> (id={device.id}) — 未処理 {count} 件
                {pausedSet.has(device.id) && (
                  <span
                    style={{
                      marginLeft: 8,
                      background: '#fde68a',
                      color: '#92400e',
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontSize: 12,
                    }}
                  >
                    paused
                  </span>
                )}
                <button
                  type="button"
                  style={{ marginLeft: 12 }}
                  disabled={drain.isPending}
                  onClick={() => drain.mutate(device.id)}
                >
                  今すぐ drain
                </button>
                <button
                  type="button"
                  style={{ marginLeft: 6 }}
                  disabled={pause.isPending}
                  onClick={() => pause.mutate(device.id)}
                >
                  待機
                </button>
              </li>
            ))}
          </ul>
          {drain.error && <p className="k-error">{(drain.error as Error).message}</p>}
        </section>
      )}

      <section className="k-card">
        {list.isLoading && <p>Loading…</p>}
        {list.error && <p className="k-error">{(list.error as Error).message}</p>}
        {list.data && (
          <QueueTable
            rows={list.data}
            onDelete={(id) => {
              if (confirm('このエントリを削除しますか？')) remove.mutate(id);
            }}
          />
        )}
      </section>
    </div>
  );
}

function QueueTable({
  rows,
  onDelete,
}: {
  rows: OfflineQueueRow[];
  onDelete: (id: number) => void;
}) {
  if (rows.length === 0) {
    return <p className="k-muted">該当エントリなし。</p>;
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
          <th style={th}>id</th>
          <th style={th}>received</th>
          <th style={th}>delivered</th>
          <th style={th}>device</th>
          <th style={th}>slack channel</th>
          <th style={th}>payload (head)</th>
          <th style={th}>actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
            <td style={td}>{row.id}</td>
            <td style={td}>{new Date(row.receivedAt).toLocaleString()}</td>
            <td style={td}>
              {row.deliveredAt ? (
                <span style={{ ...badge, background: '#dcfce7', color: '#166534' }}>
                  {new Date(row.deliveredAt).toLocaleString()}
                </span>
              ) : (
                <span style={{ ...badge, background: '#fef3c7', color: '#92400e' }}>pending</span>
              )}
            </td>
            <td style={td}>{row.targetDevice?.name ?? `#${row.targetDeviceId}`}</td>
            <td style={td}>
              #{row.slackChannel?.name ?? row.slackChannelId} ({row.slackChannel?.channelId})
            </td>
            <td style={{ ...td, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.payload.slice(0, 120)}
            </td>
            <td style={td}>
              <button type="button" onClick={() => onDelete(row.id)} style={{ color: '#b91c1c' }}>
                削除
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const th = { padding: '8px 12px', fontWeight: 600, fontSize: 13 } as const;
const td = { padding: '8px 12px', fontSize: 14, verticalAlign: 'top' } as const;
const badge = {
  padding: '2px 6px',
  borderRadius: 4,
  fontSize: 12,
} as const;
